import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import Flex from '@utilities/components/Layout/components/Flex';
import { HelpIcon, IconButtonV2, toast } from '@utilities';

import {
  createChatSession,
  hydrateDynamicNarrativeFlowState,
  readFlowState,
  updateFlowState,
  updateFlowStateTopicIds,
} from '@api/narrative-projects';
import {
  readFlow,
  readNarrativeTopics,
  readNarrativeTopicsTags,
  softDeleteFlowTopic,
} from '@api/narratives';
import Narratives from '../Narratives';
import ConfirmActionModal from './components/ConfirmActionModal';
import NarrativesCanvas from './components/NarrativesCanvas';
import NarrativesChatLandingPage from './components/NarrativesChatLandingPage';
import NarrativesChatWindow from './components/NarrativesChatWindow';
import NarrativesNavigation from './components/NarrativesNavigation';
import NarrativesTemplatesWindow from './components/NarrativesTemplatesWindow';
import NarrativesProjectHistory from './components/NarrativesProjectHistory';
import NarrativesTopicsWindow from './components/NarrativesTopicsWindow';

import { HELP_CENTER_BASE_URL } from '../Narratives/utilities/constants';
import { DEFAULT_DRAFT_NARRATIVE_NAME, PAGES, WORKSPACE_PAGES } from './utilities/constants';
import {
  areSameOrderedIds,
  buildInitialSessionState,
  buildTopicConfiguration,
  buildTopicConfigurationLookup,
  checkIsTopicCardCountMax,
  findFirstAvailableVariantSelection,
  getFlowTopicIds,
  getFlowTopicsSignature,
  getRunTopicComponentId,
  getTemplateTopicIds,
  mergeTopicIds,
  trackTopicCardAddRemove,
} from './utilities/helpers';

import styles from './_index.module.scss';

const NarrativesChat = ({ hasChatAccess }) => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const chatSessionIdFromUrl = searchParams.get('sessionId');
  const requestedInitialFlowId = location.state?.initialFlowId ?? null;
  const defaultLandingPage = hasChatAccess ? PAGES.LANDING : PAGES.TEMPLATES;
  const getInitialActivePage = () => {
    if (hasChatAccess) {
      return chatSessionIdFromUrl ? PAGES.CHAT : PAGES.LANDING;
    }
    return chatSessionIdFromUrl ? PAGES.PAST_PROJECTS : PAGES.TEMPLATES;
  };
  const [activePage, setActivePage] = useState(getInitialActivePage);
  const [flowId, setFlowId] = useState(null);
  const [draftFlowState, setDraftFlowState] = useState(null);
  const [initialInput, setInitialInput] = useState('');
  const [projectNamesBySession, setProjectNamesBySession] = useState({});
  const [draftNarrativeNamesBySession, setDraftNarrativeNamesBySession] = useState({});
  const [deletedNarrativeIdsBySession, setDeletedNarrativeIdsBySession] = useState({});
  const [projectNarrativesBySession, setProjectNarrativesBySession] = useState({});
  const [favoriteStatusBySession, setFavoriteStatusBySession] = useState({});
  const [narrativeNamesById, setNarrativeNamesById] = useState({});
  const [optimisticNarrativesBySession, setOptimisticNarrativesBySession] = useState({});
  const [runFlowTopicIds, setRunFlowTopicIds] = useState(null);
  const [sessionState, setSessionState] = useState(() => ({
    ...buildInitialSessionState(),
    chatSessionId: chatSessionIdFromUrl || null,
  }));
  const hasRequestedInitialFlowSelection =
    requestedInitialFlowId != null && chatSessionIdFromUrl === sessionState.chatSessionId;
  const [selectedTopicCards, setSelectedTopicCards] = useState([]);
  const [activeTopicConfiguration, setActiveTopicConfiguration] = useState(null);
  const [narrativeTopicGroups, setNarrativeTopicGroups] = useState(null);
  const [visibleTopicGroups, setVisibleTopicGroups] = useState(null);
  const [chatSnapshot, setChatSnapshot] = useState(null);
  const [runTopicPendingDeletion, setRunTopicPendingDeletion] = useState(null);
  const [isDeletingRunTopic, setIsDeletingRunTopic] = useState(false);
  const [runFlowRefreshKey, setRunFlowRefreshKey] = useState(0);
  const [isLeftPaneCollapsed, setIsLeftPaneCollapsed] = useState(
    chatSessionIdFromUrl ? true : false
  );
  const [workspaceTemplateContext, setWorkspaceTemplateContext] = useState({
    activeTab: 'numerator',
    selectedTemplateId: null,
  });
  const [customTemplatesRefreshKey, setCustomTemplatesRefreshKey] = useState(0);
  const [pendingInsertTopicCard, setPendingInsertTopicCard] = useState(null);
  const [isInsertingTopicCard, setIsInsertingTopicCard] = useState(false);
  const [pendingTemplateSelection, setPendingTemplateSelection] = useState(null);
  const [isTemplateDirty, setIsTemplateDirty] = useState(true);
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [hasTagLoadError, setHasTagLoadError] = useState(false);
  const [hasLoadedTopicTags, setHasLoadedTopicTags] = useState(false);
  const creatingChatSessionPromiseRef = useRef(null);
  const breakawaySyncRequestIdRef = useRef(0);
  const breakawayAddRequestIdRef = useRef(0);
  const runFlowPromptAnswersRef = useRef({});
  const narrativeTopicGroupsRequestRef = useRef(null);
  const narrativeTopicGroupsRequestIdRef = useRef(0);
  const narrativeTopicTagsRequestRef = useRef(null);
  const tagFilterRequestIdRef = useRef(0);
  const templateApplyRequestIdRef = useRef(0);
  const previousChatSessionIdFromUrlRef = useRef(chatSessionIdFromUrl);
  const previousRequestedFlowSelectionRef = useRef(null);
  const previousDraftFlowStateRef = useRef(draftFlowState);
  const pendingInsertTopicCardResolverRef = useRef(null);
  const setSynchronizedDraftFlowState = useCallback((nextFlowState) => {
    const synchronizedFlowState =
      typeof nextFlowState === 'function'
        ? nextFlowState(previousDraftFlowStateRef.current)
        : nextFlowState;

    previousDraftFlowStateRef.current = synchronizedFlowState;
    setDraftFlowState(synchronizedFlowState);
  }, []);
  const isLanding = activePage === PAGES.LANDING;
  const isTemplates = activePage === PAGES.TEMPLATES;
  const isChat = activePage === PAGES.CHAT;
  const isTopics = activePage === PAGES.TOPICS;
  const isWorkspaceTemplates = activePage === PAGES.WORKSPACE_TEMPLATES;
  const isPastProjects = activePage === PAGES.PAST_PROJECTS;
  const shouldDisableBodyScroll = !isLanding && !isTemplates;
  const shouldShowWorkspace = isChat || isTopics || isPastProjects || isWorkspaceTemplates;
  const hasSelectedTopics = selectedTopicCards.length > 0;
  const hasWorkspaceContent = Boolean(flowId) || Boolean(draftFlowState) || hasSelectedTopics;
  const shouldAllowCanvasHydrationOnMount =
    !hasWorkspaceContent && !hasRequestedInitialFlowSelection;
  const websocketFlows = useSelector((state) => state?.websocket?.flows);
  const activeRunFlow = useMemo(() => {
    if (!flowId || !Array.isArray(websocketFlows)) return null;
    return websocketFlows.find((flow) => Number(flow?.id) === Number(flowId)) || null;
  }, [flowId, websocketFlows]);

  const flowStateTopicIds = useMemo(() => {
    if (!Array.isArray(draftFlowState?.topics)) return [];
    return draftFlowState.topics.filter((topicId) => topicId != null);
  }, [draftFlowState]);

  const selectedTopicIds = useMemo(() => {
    const manualIds = selectedTopicCards.map((topic) => topic.id).filter((id) => id != null);
    if (flowId) {
      return flowStateTopicIds.length ? flowStateTopicIds : manualIds;
    }
    if (!flowStateTopicIds.length) {
      return manualIds;
    }
    return Array.from(new Set([...flowStateTopicIds, ...manualIds]));
  }, [flowId, flowStateTopicIds, selectedTopicCards]);
  const hasCanvasCards = useMemo(() => {
    if (flowId) return true;
    return selectedTopicIds.length > 0;
  }, [flowId, selectedTopicIds]);
  const topicIdsForLibrary = useMemo(() => {
    if (flowId) {
      if (Array.isArray(runFlowTopicIds) && runFlowTopicIds.length > 0) {
        return runFlowTopicIds;
      }

      const activeRunTopicIds = Array.isArray(activeRunFlow?.topics)
        ? activeRunFlow.topics
            .map((topic) => getRunTopicComponentId(topic))
            .filter((componentId) => componentId != null)
        : [];

      if (activeRunTopicIds.length > 0) {
        return activeRunTopicIds;
      }
    }

    return selectedTopicIds;
  }, [activeRunFlow?.topics, flowId, runFlowTopicIds, selectedTopicIds]);
  const topicConfigurationLookup = useMemo(
    () => buildTopicConfigurationLookup(narrativeTopicGroups),
    [narrativeTopicGroups]
  );

  const normalizedSelectedTags = useMemo(
    () => Array.from(new Set(selectedTags.filter(Boolean))).sort(),
    [selectedTags]
  );
  const selectedTagsKey = useMemo(() => normalizedSelectedTags.join(','), [normalizedSelectedTags]);

  const shouldLoadNarrativeTopicGroups = isTopics || hasCanvasCards;
  const activeChatSnapshot = useMemo(() => {
    if (!chatSnapshot) return null;
    if (chatSnapshot.chatSessionId !== sessionState.chatSessionId) return null;
    return chatSnapshot;
  }, [chatSnapshot, sessionState.chatSessionId]);

  const replaceChatSessionQueryParam = useCallback(
    (chatSessionId) => {
      const params = new URLSearchParams(searchParams);
      if (chatSessionId) {
        params.set('sessionId', chatSessionId);
      } else {
        params.delete('sessionId');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const reconcileDraftFlowState = useCallback(async ({ chatSessionId, fallbackFlowState }) => {
    if (!chatSessionId) return fallbackFlowState;

    try {
      const latestFlowStateResponse = await readFlowState({ chatSessionId });
      const reconciledFlowState = latestFlowStateResponse?.flowState;
      if (!reconciledFlowState) return fallbackFlowState;
      // updateFlowStateTopicIds only persists topics, so the re-read can still carry a
      // stale run-linked focusFlowId. Keep the draft detached so it stays in Past Projects.
      return { ...reconciledFlowState, focusFlowId: null };
    } catch (error) {
      if (error?.name !== 'AbortError' && error?.name !== 'CanceledError') {
        console.error('Failed to re-read draft flow state after sync', error);
      }
      return fallbackFlowState;
    }
  }, []);

  const resetSessionState = useCallback(() => {
    setSessionState(buildInitialSessionState());
    setChatSnapshot(null);
    setFlowId(null);
    setSynchronizedDraftFlowState(null);
    setRunFlowTopicIds(null);
    setIsDeletingRunTopic(false);
    setIsLeftPaneCollapsed(false);
    setRunFlowRefreshKey(0);
    setRunTopicPendingDeletion(null);
    setActiveTopicConfiguration(null);
    setSelectedTopicCards([]);
    setWorkspaceTemplateContext({ activeTab: 'numerator', selectedTemplateId: null });
    setCustomTemplatesRefreshKey(0);
    if (pendingInsertTopicCardResolverRef.current) {
      pendingInsertTopicCardResolverRef.current(false);
      pendingInsertTopicCardResolverRef.current = null;
    }
    setPendingInsertTopicCard(null);
    setPendingTemplateSelection(null);
    setNarrativeNamesById({});
    setProjectNamesBySession({});
    setDraftNarrativeNamesBySession({});
    setProjectNarrativesBySession({});
    setFavoriteStatusBySession({});
    setIsTemplateDirty(true);
    replaceChatSessionQueryParam(null);
    setSelectedTags([]);
    setNarrativeTopicGroups(null);
    setVisibleTopicGroups(null);
    setAvailableTags([]);
    setHasTagLoadError(false);
    setHasLoadedTopicTags(false);
    tagFilterRequestIdRef.current = 0;
  }, [replaceChatSessionQueryParam]);

  const resetCanvasSelectionState = useCallback(
    ({ shouldResetTopicFilters = false } = {}) => {
      setSelectedTopicCards([]);
      setSynchronizedDraftFlowState(null);
      setActiveTopicConfiguration(null);
      setRunFlowTopicIds(null);
      runFlowPromptAnswersRef.current = {};

      if (shouldResetTopicFilters) {
        setSelectedTags([]);
        setVisibleTopicGroups(narrativeTopicGroups);
        tagFilterRequestIdRef.current += 1;
      }
    },
    [narrativeTopicGroups]
  );

  const handleChatSessionReady = useCallback(
    (sessionId) => {
      if (!sessionId) return;
      if (sessionState.chatSessionId && sessionState.chatSessionId !== sessionId) {
        setActiveTopicConfiguration(null);
        setSynchronizedDraftFlowState(null);
        setFlowId(null);
        setRunFlowTopicIds(null);
        setSelectedTopicCards([]);
        runFlowPromptAnswersRef.current = {};
      }
      setSessionState((prev) => {
        if (prev.chatSessionId === sessionId) return prev;
        return { ...prev, chatSessionId: sessionId };
      });
      replaceChatSessionQueryParam(sessionId);
      setActivePage((prev) => (prev === PAGES.LANDING ? PAGES.CHAT : prev));
    },
    [replaceChatSessionQueryParam, sessionState.chatSessionId]
  );

  const handleFlowSelected = useCallback(
    (nextFlowId, options = {}) => {
      if (
        options?.source === 'chat-hydration' &&
        hasRequestedInitialFlowSelection &&
        Number(nextFlowId) !== Number(requestedInitialFlowId)
      ) {
        return;
      }

      setActiveTopicConfiguration(null);
      setFlowId(nextFlowId);
      setRunFlowTopicIds(null);
      runFlowPromptAnswersRef.current = {};
    },
    [hasRequestedInitialFlowSelection, requestedInitialFlowId]
  );

  const handleDraftSelected = useCallback((nextFlowState) => {
    setActiveTopicConfiguration(null);
    setFlowId(null);
    setRunFlowTopicIds(null);
    setSelectedTopicCards([]);
    setSynchronizedDraftFlowState(nextFlowState || null);
    setIsTemplateDirty(true);
  }, []);

  const handleFlowStateChange = useCallback(
    (nextFlowState, options = {}) => {
      if (!options.shouldBreakAwayFromRun && nextFlowState == null) {
        setSynchronizedDraftFlowState(null);
        setIsTemplateDirty(true);
        return;
      }

      if (!options.shouldBreakAwayFromRun || !nextFlowState) {
        const previousFlowState = previousDraftFlowStateRef.current;
        const normalizedFlowState = {
          ...(previousFlowState || {}),
          ...(nextFlowState || {}),
          narrativeName:
            nextFlowState?.narrativeName ||
            previousFlowState?.narrativeName ||
            DEFAULT_DRAFT_NARRATIVE_NAME,
        };
        const didComponentsChange =
          getFlowTopicsSignature(previousFlowState) !== getFlowTopicsSignature(normalizedFlowState);
        const didNarrativeNameChange =
          (previousFlowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME) !==
          (normalizedFlowState?.narrativeName || DEFAULT_DRAFT_NARRATIVE_NAME);

        if (didComponentsChange || didNarrativeNameChange) {
          setIsTemplateDirty(true);
        }

        const isSourceOfChangeChat = options?.sourceOfChange === 'chat';
        if (isSourceOfChangeChat) {
          const prevFlowStateTopics = getFlowTopicIds(previousFlowState);
          const nextFlowStateTopics = getFlowTopicIds(normalizedFlowState);

          const addedTopicIds = nextFlowStateTopics?.filter(
            (topic) => !prevFlowStateTopics?.includes(topic)
          );

          const removedTopicIds = prevFlowStateTopics?.filter(
            (topic) => !nextFlowStateTopics?.includes(topic)
          );
          if (addedTopicIds?.length) {
            addedTopicIds.forEach((topicId) => {
              const topicName = topicConfigurationLookup[topicId]?.name ?? 'n/a';
              trackTopicCardAddRemove({
                action: 'add',
                chatSessionId: sessionState.chatSessionId,
                environmentState: 'draft',
                sourceOfChange: 'chat',
                topicId,
                topicName,
              });
            });
          }
          if (removedTopicIds?.length) {
            removedTopicIds.forEach((topicId) => {
              const topicName = topicConfigurationLookup[topicId]?.name ?? 'n/a';

              trackTopicCardAddRemove({
                action: 'remove',
                chatSessionId: sessionState.chatSessionId,
                environmentState: 'draft',
                sourceOfChange: 'chat',
                topicId,
                topicName,
              });
            });
          }
        }

        setSynchronizedDraftFlowState(normalizedFlowState);
        return;
      }

      let nextDraftFlowState = nextFlowState;
      const fallbackRunTopicIds = Array.isArray(activeRunFlow?.topics)
        ? activeRunFlow.topics
            .filter((topic) => !topic?.isHidden)
            .map((topic) => getRunTopicComponentId(topic))
            .filter((componentId) => componentId != null)
        : null;
      const baseRunTopicIds = Array.isArray(runFlowTopicIds)
        ? runFlowTopicIds
        : fallbackRunTopicIds;
      if (Array.isArray(baseRunTopicIds) && Array.isArray(nextFlowState.topics)) {
        const runTopicIdSet = new Set(baseRunTopicIds.map(String));
        const updatedTopicIdSet = new Set(getFlowTopicIds(nextFlowState));
        const removedTopicIds = baseRunTopicIds
          .map(String)
          .filter((topicId) => !updatedTopicIdSet.has(topicId));
        if (removedTopicIds.length) {
          removedTopicIds.forEach((topicId) => {
            const topicName = topicConfigurationLookup[topicId]?.name ?? 'n/a';
            trackTopicCardAddRemove({
              action: 'remove',
              chatSessionId: sessionState.chatSessionId,
              environmentState: 'completed_narrative',
              flowId,
              sourceOfChange: 'chat',
              topicId,
              topicName,
            });
          });
        }
        const addedTopicIds = nextFlowState.topics.filter(
          (topicId) => !runTopicIdSet.has(String(topicId))
        );
        const nextTopicIds = mergeTopicIds(baseRunTopicIds, addedTopicIds);

        nextDraftFlowState = {
          ...nextFlowState,
          focusFlowId: null,
          topics: nextTopicIds,
        };

        if (sessionState.chatSessionId && !areSameOrderedIds(nextFlowState.topics, nextTopicIds)) {
          const syncRequestId = ++breakawaySyncRequestIdRef.current;
          const expectedTopicsSignature = JSON.stringify(nextTopicIds.map(String));
          updateFlowStateTopicIds({
            chatSessionId: sessionState.chatSessionId,
            topicIds: nextTopicIds,
          })
            .then(() =>
              reconcileDraftFlowState({
                chatSessionId: sessionState.chatSessionId,
                fallbackFlowState: nextDraftFlowState,
              })
            )
            .then((updatedFlowState) => {
              if (syncRequestId !== breakawaySyncRequestIdRef.current) return;
              setSynchronizedDraftFlowState((currentFlowState) => {
                const currentTopics = Array.isArray(currentFlowState?.topics)
                  ? currentFlowState.topics
                  : [];
                if (expectedTopicsSignature !== JSON.stringify(currentTopics.map(String))) {
                  return currentFlowState;
                }
                return updatedFlowState || nextDraftFlowState;
              });
            })
            .catch((error) => {
              console.error('Failed to sync draft flow state after chat breakaway', error);
              toast.error('We could not sync the draft topic cards. Please refresh and try again.');
            });
        }
      }

      setSynchronizedDraftFlowState((previousFlowState) => ({
        ...(previousFlowState || {}),
        ...(nextDraftFlowState || {}),
        focusFlowId: null,
        narrativeName:
          nextDraftFlowState?.narrativeName ||
          previousFlowState?.narrativeName ||
          DEFAULT_DRAFT_NARRATIVE_NAME,
      }));
      setActiveTopicConfiguration(null);
      setFlowId(null);
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);
    },
    [
      activeRunFlow,
      flowId,
      reconcileDraftFlowState,
      runFlowTopicIds,
      sessionState.chatSessionId,
      topicConfigurationLookup,
    ]
  );

  const ensureChatSession = useCallback(async () => {
    if (sessionState.chatSessionId) return sessionState.chatSessionId;
    if (creatingChatSessionPromiseRef.current) return creatingChatSessionPromiseRef.current;

    const createPromise = (async () => {
      try {
        const sessionId = await createChatSession();
        if (!sessionId) throw new Error('Chat service did not return a session identifier.');
        handleChatSessionReady(sessionId);
        return sessionId;
      } catch (error) {
        console.error('Failed to create chat session from topics workspace', error);
        toast.error('We could not start your project. Please try again.');
        return null;
      } finally {
        creatingChatSessionPromiseRef.current = null;
      }
    })();

    creatingChatSessionPromiseRef.current = createPromise;
    return createPromise;
  }, [handleChatSessionReady, sessionState.chatSessionId]);

  const loadNarrativeTopicGroups = useCallback(async () => {
    if (Array.isArray(narrativeTopicGroups)) {
      return narrativeTopicGroups;
    }

    if (narrativeTopicGroupsRequestRef.current) {
      return narrativeTopicGroupsRequestRef.current;
    }

    const requestId = ++narrativeTopicGroupsRequestIdRef.current;
    const request = (async () => {
      try {
        const topics = await readNarrativeTopics({ signal: undefined });
        const normalizedTopics = Array.isArray(topics) ? topics : [];
        if (narrativeTopicGroupsRequestIdRef.current !== requestId) return normalizedTopics;
        setNarrativeTopicGroups(normalizedTopics);
        return normalizedTopics;
      } catch (error) {
        if (narrativeTopicGroupsRequestIdRef.current !== requestId) return [];
        console.error('Error fetching narrative topics:', error);
        setNarrativeTopicGroups([]);
        toast.error('Error loading topics. Please refresh the page or try again.');
        return [];
      } finally {
        if (narrativeTopicGroupsRequestIdRef.current === requestId) {
          narrativeTopicGroupsRequestRef.current = null;
        }
      }
    })();

    narrativeTopicGroupsRequestRef.current = request;
    return request;
  }, [narrativeTopicGroups]);

  const handleRunFlowTopicIdsChange = useCallback((nextTopicIds = [], promptAnswers = {}) => {
    runFlowPromptAnswersRef.current = promptAnswers;
    setRunFlowTopicIds((previousTopicIds) => {
      if (!Array.isArray(previousTopicIds)) return nextTopicIds;

      return areSameOrderedIds(previousTopicIds, nextTopicIds) ? previousTopicIds : nextTopicIds;
    });
  }, []);

  const handleProjectNameSync = useCallback((chatSessionId, name) => {
    if (!chatSessionId || typeof name !== 'string') return;

    setProjectNamesBySession((previousNames) => {
      if (previousNames?.[chatSessionId] === name) return previousNames;
      return { ...previousNames, [chatSessionId]: name };
    });
  }, []);

  const handleProjectFavoriteSync = useCallback((chatSessionId, isFavorite) => {
    if (!chatSessionId) return;

    setFavoriteStatusBySession((previousFavorites) => {
      if (previousFavorites?.[chatSessionId] === isFavorite) return previousFavorites;
      return { ...previousFavorites, [chatSessionId]: isFavorite };
    });
  }, []);

  const handleProjectNarrativeCreated = useCallback(({ chatSessionId, flowId, name }) => {
    if (!chatSessionId || !flowId) return;

    setOptimisticNarrativesBySession((previousNarratives) => {
      const sessionNarratives = previousNarratives?.[chatSessionId] || [];
      if (sessionNarratives.some((narrative) => Number(narrative.flowId) === Number(flowId))) {
        return previousNarratives;
      }

      return {
        ...previousNarratives,
        [chatSessionId]: [
          ...sessionNarratives,
          {
            createdAt: new Date().toISOString(),
            flowId: Number(flowId),
            isDeleted: false,
            name: name || 'Untitled Narrative',
            status: 'RUNNING',
          },
        ],
      };
    });

    setDeletedNarrativeIdsBySession((previousDeletedIds) => ({
      ...previousDeletedIds,
      [chatSessionId]: (previousDeletedIds?.[chatSessionId] || []).filter(
        (deletedId) => Number(deletedId) !== Number(flowId)
      ),
    }));

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const existingNarratives = Array.isArray(previousNarrativesBySession?.[chatSessionId])
        ? previousNarrativesBySession[chatSessionId]
        : [];

      const flowIdAsNumber = Number(flowId);
      const nextNarrative = {
        createdDatetime: null,
        id: flowIdAsNumber,
        isOwner: true,
        name: name || 'Untitled Narrative',
        status: 'RUNNING',
      };

      const existingIndex = existingNarratives.findIndex(
        (narrative) => Number(narrative?.id) === flowIdAsNumber
      );

      let nextNarratives;
      if (existingIndex > -1) {
        nextNarratives = [...existingNarratives];
        nextNarratives[existingIndex] = {
          ...nextNarratives[existingIndex],
          ...nextNarrative,
        };
      } else {
        nextNarratives = [nextNarrative, ...existingNarratives];
      }

      return {
        ...previousNarrativesBySession,
        [chatSessionId]: nextNarratives,
      };
    });
  }, []);

  const handleProjectNarrativeDeleted = useCallback(
    ({ chatSessionId, flowId: deletedFlowId, shouldClearCanvasState, shouldClearRunFlowState }) => {
      if (!chatSessionId || deletedFlowId == null) return;

      setDeletedNarrativeIdsBySession((previousDeletedIds) => ({
        ...previousDeletedIds,
        [chatSessionId]: Array.from(
          new Set([...(previousDeletedIds?.[chatSessionId] || []), Number(deletedFlowId)])
        ),
      }));
      setProjectNarrativesBySession((previousNarrativesBySession) => ({
        ...previousNarrativesBySession,
        [chatSessionId]: (previousNarrativesBySession?.[chatSessionId] || []).filter(
          (narrative) => Number(narrative?.id) !== Number(deletedFlowId)
        ),
      }));
      setOptimisticNarrativesBySession((previousNarrativesBySession) => ({
        ...previousNarrativesBySession,
        [chatSessionId]: (previousNarrativesBySession?.[chatSessionId] || []).map((narrative) =>
          Number(narrative?.flowId) === Number(deletedFlowId)
            ? { ...narrative, isDeleted: true }
            : narrative
        ),
      }));
      setNarrativeNamesById((previousNamesById) => {
        if (!Object.prototype.hasOwnProperty.call(previousNamesById, deletedFlowId)) {
          return previousNamesById;
        }
        const nextNamesById = { ...previousNamesById };
        delete nextNamesById[deletedFlowId];
        return nextNamesById;
      });

      if (
        sessionState.chatSessionId === chatSessionId &&
        (shouldClearCanvasState || shouldClearRunFlowState)
      ) {
        setSynchronizedDraftFlowState(null);
        setActiveTopicConfiguration(null);
        setSelectedTopicCards([]);
        setDraftNarrativeNamesBySession((previousNames) => {
          if (!Object.prototype.hasOwnProperty.call(previousNames, chatSessionId)) {
            return previousNames;
          }
          const nextNames = { ...previousNames };
          delete nextNames[chatSessionId];
          return nextNames;
        });
      }
    },
    [sessionState.chatSessionId, setSynchronizedDraftFlowState]
  );

  const handleProjectNarrativeRenamed = useCallback(
    ({ chatSessionId, flowId: renamedFlowId, name }) => {
      if (!chatSessionId || !renamedFlowId || typeof name !== 'string') return;
      setNarrativeNamesById((previousNamesById) => {
        if (previousNamesById?.[renamedFlowId] === name) return previousNamesById;
        return { ...previousNamesById, [renamedFlowId]: name };
      });

      setProjectNarrativesBySession((previousNarrativesBySession) => {
        const sessionNarratives = Array.isArray(previousNarrativesBySession?.[chatSessionId])
          ? previousNarrativesBySession[chatSessionId]
          : [];

        let didChange = false;
        const nextSessionNarratives = sessionNarratives.map((narrative) => {
          if (Number(narrative?.id) !== Number(renamedFlowId)) return narrative;
          if (narrative?.name === name) return narrative;
          didChange = true;
          return { ...narrative, name };
        });

        if (!didChange) return previousNarrativesBySession;

        return {
          ...previousNarrativesBySession,
          [chatSessionId]: nextSessionNarratives,
        };
      });
      setOptimisticNarrativesBySession((previousNarrativesBySession) => ({
        ...previousNarrativesBySession,
        [chatSessionId]: (previousNarrativesBySession?.[chatSessionId] || []).map((narrative) =>
          Number(narrative?.flowId) === Number(renamedFlowId) ? { ...narrative, name } : narrative
        ),
      }));
    },
    []
  );

  const handleProjectDraftDeleted = useCallback(
    (chatSessionId) => {
      if (!chatSessionId || sessionState.chatSessionId !== chatSessionId) return;

      setSynchronizedDraftFlowState(null);
      if (flowId == null) {
        setActiveTopicConfiguration(null);
        setSelectedTopicCards([]);
      }
      setDraftNarrativeNamesBySession((previousNames) => {
        if (!Object.prototype.hasOwnProperty.call(previousNames, chatSessionId)) {
          return previousNames;
        }
        const nextNames = { ...previousNames };
        delete nextNames[chatSessionId];
        return nextNames;
      });
    },
    [flowId, sessionState.chatSessionId]
  );

  useEffect(() => {
    if (!sessionState.chatSessionId || flowId != null) return;
    if (typeof draftFlowState?.narrativeName !== 'string') return;

    setDraftNarrativeNamesBySession((previousNames) => {
      if (previousNames?.[sessionState.chatSessionId] === draftFlowState.narrativeName) {
        return previousNames;
      }
      return {
        ...previousNames,
        [sessionState.chatSessionId]: draftFlowState.narrativeName,
      };
    });
  }, [draftFlowState?.narrativeName, flowId, sessionState.chatSessionId]);

  useEffect(() => {
    if (!Array.isArray(websocketFlows) || websocketFlows.length === 0) return;
    const websocketFlowsById = new Map(
      websocketFlows.map((flow) => [Number(flow?.id), flow]).filter(([id]) => !Number.isNaN(id))
    );

    setProjectNarrativesBySession((previousNarrativesBySession) => {
      const nextNarrativesBySession = {};
      let didChange = false;

      Object.entries(previousNarrativesBySession).forEach(([chatSessionId, narratives]) => {
        if (!Array.isArray(narratives) || narratives.length === 0) {
          nextNarrativesBySession[chatSessionId] = narratives;
          return;
        }

        const nextNarratives = narratives.map((narrative) => {
          const matchedFlow = websocketFlowsById.get(Number(narrative?.id));
          if (!matchedFlow) return narrative;

          const nextStatus = matchedFlow?.status || narrative?.status;
          const nextNarrative = {
            ...narrative,
            createdDatetime: matchedFlow?.createdDatetime || narrative?.createdDatetime || null,
            name: matchedFlow?.name || narrative?.name || 'Untitled Narrative',
            status: nextStatus,
          };

          const hasChanged =
            nextNarrative.createdDatetime !== narrative?.createdDatetime ||
            nextNarrative.name !== narrative?.name ||
            nextNarrative.status !== narrative?.status;

          if (hasChanged) {
            didChange = true;
          }

          return hasChanged ? nextNarrative : narrative;
        });

        nextNarrativesBySession[chatSessionId] = nextNarratives;
      });

      return didChange ? nextNarrativesBySession : previousNarrativesBySession;
    });
  }, [websocketFlows]);

  const breakAwayToDraftFlowState = useCallback(
    async ({ topic }) => {
      if (!topic?.id) return false;

      const isViewingRun = flowId != null;
      const previousFlowState = isViewingRun ? null : draftFlowState;
      const inheritedPromptAnswers = isViewingRun
        ? runFlowPromptAnswersRef.current
        : (previousFlowState?.editablePromptAnswers ?? null);

      const chatSessionId = await ensureChatSession();
      if (!chatSessionId) return false;

      let baseTopicIds = [];
      if (Array.isArray(runFlowTopicIds)) {
        baseTopicIds = runFlowTopicIds;
      } else if (Array.isArray(activeRunFlow?.topics)) {
        baseTopicIds = activeRunFlow.topics
          .filter((topicItem) => !topicItem?.isHidden)
          .map((topicItem) => getRunTopicComponentId(topicItem))
          .filter((componentId) => componentId != null);
      } else if (Array.isArray(draftFlowState?.topics)) {
        baseTopicIds = draftFlowState.topics;
      }
      const nextComponentIds = mergeTopicIds(baseTopicIds, [topic.id]);
      const previousFlowId = flowId;
      const previousSelectedTopicCards = selectedTopicCards;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, focusFlowId: null, topics: nextComponentIds }
        : {
            editablePromptAnswers: inheritedPromptAnswers,
            focusFlowId: null,
            topics: nextComponentIds,
          };
      const requestId = ++breakawayAddRequestIdRef.current;

      setSynchronizedDraftFlowState(optimisticFlowState);
      setActiveTopicConfiguration(null);
      setFlowId(null);
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);

      try {
        const { narrativeName, ...flowStateForUpdate } = optimisticFlowState;
        await updateFlowState({ chatSessionId, flowState: flowStateForUpdate });
        trackTopicCardAddRemove({
          action: 'add',
          chatSessionId,
          environmentState: 'draft',
          sourceOfChange: 'manual',
          topicId: topic.id,
          topicName: topic.name,
        });
        trackTopicCardAddRemove({
          action: 'add',
          chatSessionId,
          environmentState: 'draft',
          sourceOfChange: 'manual',
          topicId: topic.id,
          topicName: topic.name,
        });
        const updatedFlowState = await reconcileDraftFlowState({
          chatSessionId,
          fallbackFlowState: optimisticFlowState,
        });

        if (requestId !== breakawayAddRequestIdRef.current) return false;
        setSynchronizedDraftFlowState(updatedFlowState);
        return true;
      } catch (error) {
        if (requestId !== breakawayAddRequestIdRef.current) return false;
        console.error('Failed to create draft flow state from run Narrative', error);
        setSynchronizedDraftFlowState(previousFlowState);
        setActiveTopicConfiguration(null);
        setFlowId(previousFlowId);
        setSelectedTopicCards(previousSelectedTopicCards);
        toast.error('We could not add that topic card. Please try again.');
        return false;
      }
    },
    [
      activeRunFlow,
      draftFlowState,
      ensureChatSession,
      flowId,
      reconcileDraftFlowState,
      runFlowTopicIds,
      selectedTopicCards,
    ]
  );

  const requestInsertTopicCardBreakaway = useCallback(({ topic }) => {
    if (!topic?.id) return Promise.resolve(false);

    if (pendingInsertTopicCardResolverRef.current) {
      pendingInsertTopicCardResolverRef.current(false);
    }

    setIsInsertingTopicCard(false);
    return new Promise((resolve) => {
      pendingInsertTopicCardResolverRef.current = resolve;
      setPendingInsertTopicCard({ topic });
    });
  }, []);

  const closePendingInsertTopicCard = useCallback((result = false) => {
    setIsInsertingTopicCard(false);
    const resolver = pendingInsertTopicCardResolverRef.current;
    pendingInsertTopicCardResolverRef.current = null;
    setPendingInsertTopicCard(null);
    resolver?.(result);
  }, []);

  const confirmPendingInsertTopicCard = useCallback(async () => {
    if (isInsertingTopicCard) return;

    if (!pendingInsertTopicCard?.topic) {
      closePendingInsertTopicCard(false);
      return;
    }

    setIsInsertingTopicCard(true);
    const wasInserted = await breakAwayToDraftFlowState({
      topic: pendingInsertTopicCard.topic,
    });
    closePendingInsertTopicCard(wasInserted);
  }, [
    breakAwayToDraftFlowState,
    closePendingInsertTopicCard,
    isInsertingTopicCard,
    pendingInsertTopicCard,
  ]);

  const breakAwayToDraftFlowStateWithReplacement = useCallback(
    async ({ nextTopic, previousTopicId }) => {
      if (!nextTopic?.id || previousTopicId == null) return false;

      const isViewingRun = flowId != null;
      const previousFlowState = isViewingRun ? null : draftFlowState;
      const inheritedPromptAnswers = isViewingRun
        ? runFlowPromptAnswersRef.current
        : (previousFlowState?.editablePromptAnswers ?? null);

      const chatSessionId = await ensureChatSession();
      if (!chatSessionId) return false;

      let baseTopicIds = [];
      if (Array.isArray(runFlowTopicIds)) {
        baseTopicIds = runFlowTopicIds;
      } else if (Array.isArray(activeRunFlow?.topics)) {
        baseTopicIds = activeRunFlow.topics
          .filter((topicItem) => !topicItem?.isHidden)
          .map((topicItem) => getRunTopicComponentId(topicItem))
          .filter((componentId) => componentId != null);
      } else if (Array.isArray(draftFlowState?.topics)) {
        baseTopicIds = draftFlowState.topics;
      }

      const nextComponentIds = baseTopicIds.map((topicId) =>
        String(topicId) === String(previousTopicId) ? nextTopic.id : topicId
      );
      const previousFlowId = flowId;
      const previousSelectedTopicCards = selectedTopicCards;
      const optimisticFlowState = previousFlowState
        ? { ...previousFlowState, focusFlowId: null, topics: nextComponentIds }
        : {
            editablePromptAnswers: inheritedPromptAnswers,
            focusFlowId: null,
            topics: nextComponentIds,
          };
      const requestId = ++breakawayAddRequestIdRef.current;

      setSynchronizedDraftFlowState(optimisticFlowState);
      setActiveTopicConfiguration(null);
      setFlowId(null);
      setSelectedTopicCards([]);
      setIsTemplateDirty(true);

      try {
        const { narrativeName, ...flowStateForUpdate } = optimisticFlowState;
        await updateFlowState({ chatSessionId, flowState: flowStateForUpdate });
        const updatedFlowState = await reconcileDraftFlowState({
          chatSessionId,
          fallbackFlowState: optimisticFlowState,
        });

        if (requestId !== breakawayAddRequestIdRef.current) return false;
        setSynchronizedDraftFlowState(updatedFlowState);
        return true;
      } catch (error) {
        if (requestId !== breakawayAddRequestIdRef.current) return false;
        console.error('Failed to replace run topic card in draft Narrative', error);
        setSynchronizedDraftFlowState(previousFlowState);
        setActiveTopicConfiguration(null);
        setFlowId(previousFlowId);
        setSelectedTopicCards(previousSelectedTopicCards);
        toast.error('We could not update that topic card. Please try again.');
        return false;
      }
    },
    [
      activeRunFlow,
      draftFlowState,
      ensureChatSession,
      flowId,
      reconcileDraftFlowState,
      runFlowTopicIds,
      selectedTopicCards,
    ]
  );

  const handleInsertTopicCard = useCallback(
    async (group) => {
      if (!group?.grouping || !Array.isArray(group?.topics) || group.topics.length === 0) return;
      const addedIdSet = new Set(topicIdsForLibrary.filter((id) => id != null).map(String));
      if (checkIsTopicCardCountMax(addedIdSet.size)) return;
      const totalVariants = group.topics.length;
      const availableTopics = group.topics.filter((topic) => !addedIdSet.has(String(topic?.id)));
      if (!availableTopics.length) return;

      if (totalVariants === 1 && !availableTopics[0]?.variant) {
        const topic = availableTopics[0];
        if (flowId) {
          await requestInsertTopicCardBreakaway({ topic });
          return;
        }

        setSelectedTopicCards((prev) => {
          if (prev.some((item) => String(item.id) === String(topic.id))) return prev;
          return [...prev, { id: topic.id, name: topic.name, vizType: topic.vizType }];
        });
        setIsTemplateDirty(true);
        void (async () => {
          const chatSessionId = sessionState.chatSessionId || (await ensureChatSession());
          if (!chatSessionId) return;
          trackTopicCardAddRemove({
            action: 'add',
            chatSessionId,
            environmentState: 'draft',
            sourceOfChange: 'manual',
            topicId: topic.id,
            topicName: topic?.name ?? 'n/a',
          });
        })();
        return;
      }
      const config = buildTopicConfiguration(group);
      if (!config) return;
      const selectedVariant = findFirstAvailableVariantSelection({
        config,
        occupiedTopicIdSet: addedIdSet,
      });

      setActiveTopicConfiguration({
        config,
        id: `config:${group.grouping}`,
        mode: flowId ? 'run' : 'draft',
        name: group.grouping,
        occupiedTopicIds: Array.from(addedIdSet),
        previousTopicId: null,
        selectedVariant,
        vizType: group.topics[0]?.vizType,
      });
    },
    [
      ensureChatSession,
      flowId,
      requestInsertTopicCardBreakaway,
      sessionState.chatSessionId,
      topicIdsForLibrary,
    ]
  );

  const confirmRunTopicDeletion = useCallback(async () => {
    if (!runTopicPendingDeletion?.runTopicId) return;

    setIsDeletingRunTopic(true);
    try {
      await softDeleteFlowTopic({ topicId: runTopicPendingDeletion.runTopicId });
      let runTopics = Array.isArray(activeRunFlow?.topics) ? activeRunFlow.topics : [];
      if (!runTopics.length && flowId) {
        try {
          const runFlowResponse = await readFlow({ id: flowId });
          runTopics = Array.isArray(runFlowResponse?.topics) ? runFlowResponse.topics : [];
        } catch (error) {
          console.error('Failed to load run topics for topic sync after removal', error);
        }
      }

      const nextComponentIdsFromRun = runTopics
        .filter((topic) => String(topic?.id) !== String(runTopicPendingDeletion.runTopicId))
        .map((topic) => getRunTopicComponentId(topic))
        .filter((componentId) => componentId != null);
      const nextComponentIds =
        nextComponentIdsFromRun.length > 0
          ? nextComponentIdsFromRun
          : (Array.isArray(draftFlowState?.topics) ? draftFlowState.topics : []).filter(
              (topicId) => String(topicId) !== String(runTopicPendingDeletion.topicId)
            );

      const optimisticFlowState = draftFlowState
        ? { ...draftFlowState, focusFlowId: null, topics: nextComponentIds }
        : {
            focusFlowId: null,
            narrativeName: DEFAULT_DRAFT_NARRATIVE_NAME,
            topics: nextComponentIds,
          };
      setSynchronizedDraftFlowState(optimisticFlowState);

      if (sessionState.chatSessionId) {
        await updateFlowStateTopicIds({
          chatSessionId: sessionState.chatSessionId,
          topicIds: nextComponentIds,
        });
        const updatedFlowState = await reconcileDraftFlowState({
          chatSessionId: sessionState.chatSessionId,
          fallbackFlowState: optimisticFlowState,
        });

        setSynchronizedDraftFlowState(updatedFlowState);
      }

      setSelectedTopicCards((prev) =>
        prev.filter((item) => String(item?.id) !== String(runTopicPendingDeletion.topicId))
      );
      setRunFlowRefreshKey((prev) => prev + 1);
      setRunTopicPendingDeletion(null);
      setIsTemplateDirty(true);
    } catch (error) {
      console.error('Failed to remove run topic card from topics list', error);
      toast.error('We could not remove that topic card. Please try again.');
    } finally {
      setIsDeletingRunTopic(false);
    }
  }, [
    activeRunFlow,
    draftFlowState,
    flowId,
    reconcileDraftFlowState,
    runTopicPendingDeletion,
    sessionState.chatSessionId,
  ]);

  const handlePageChange = useCallback(
    (nextPage) => {
      if (nextPage === PAGES.TEMPLATES) {
        if (shouldShowWorkspace) {
          setActivePage(PAGES.WORKSPACE_TEMPLATES);
          setIsLeftPaneCollapsed(false);
          return;
        }

        setActivePage(PAGES.TEMPLATES);
        return;
      }

      if (nextPage === 'new-project') {
        resetSessionState();
        setActivePage(defaultLandingPage);
        return;
      }

      if (nextPage === PAGES.LANDING) {
        resetSessionState();
        setActivePage(PAGES.LANDING);
        return;
      }

      const nextShowsWorkspace = WORKSPACE_PAGES.has(nextPage);
      if (nextShowsWorkspace) {
        if (activePage === nextPage && !isLeftPaneCollapsed) {
          setIsLeftPaneCollapsed(true);
          return;
        }

        setActivePage(nextPage);
        setIsLeftPaneCollapsed(false);
        return;
      }

      if (shouldShowWorkspace) {
        resetSessionState();
      }

      setActivePage(nextPage);
    },
    [activePage, isLeftPaneCollapsed, resetSessionState, shouldShowWorkspace]
  );

  const handleChatMessageSent = useCallback(() => {
    setInitialInput('');
  }, []);

  const handleLandingSubmit = (input) => setInitialInput(input);
  const handleHelpClick = () => {
    window.open(HELP_CENTER_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  const handleChatStateSnapshot = useCallback((snapshot) => {
    setChatSnapshot(snapshot);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('no-scroll', shouldDisableBodyScroll);

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [shouldDisableBodyScroll]);

  useEffect(() => {
    if (!shouldLoadNarrativeTopicGroups) return;
    void loadNarrativeTopicGroups();
  }, [loadNarrativeTopicGroups, shouldLoadNarrativeTopicGroups]);

  useEffect(() => {
    if (
      !sessionState.chatSessionId ||
      isChat ||
      hasWorkspaceContent ||
      hasRequestedInitialFlowSelection
    ) {
      return;
    }

    const abortController = new AbortController();

    readFlowState({
      chatSessionId: sessionState.chatSessionId,
      signal: abortController.signal,
    })
      .then(({ flowId: nextFlowId, flowState: nextFlowState }) => {
        if (abortController.signal.aborted) return;

        handleFlowStateChange(nextFlowState || null);
        if (nextFlowId) {
          handleFlowSelected(nextFlowId);
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.name === 'CanceledError') return;
        console.error('Failed to hydrate workspace flow state', error);
      });

    return () => {
      abortController.abort();
    };
  }, [
    handleFlowSelected,
    handleFlowStateChange,
    hasRequestedInitialFlowSelection,
    hasWorkspaceContent,
    isChat,
    sessionState.chatSessionId,
  ]);

  useEffect(() => {
    if (selectedTagsKey) return;
    setVisibleTopicGroups(narrativeTopicGroups);
  }, [narrativeTopicGroups, selectedTagsKey]);

  useEffect(() => {
    if (!isTopics || !selectedTagsKey) {
      tagFilterRequestIdRef.current += 1;
      return;
    }

    const requestId = ++tagFilterRequestIdRef.current;
    const loadFilteredNarrativeTopicGroups = async () => {
      try {
        const topics = await readNarrativeTopics({
          signal: undefined,
          tags: selectedTagsKey.split(','),
        });
        const normalizedTopics = Array.isArray(topics) ? topics : [];
        if (tagFilterRequestIdRef.current !== requestId) {
          return;
        }
        setVisibleTopicGroups(normalizedTopics);
      } catch (error) {
        if (tagFilterRequestIdRef.current !== requestId) {
          return;
        }
        console.error('Error fetching filtered narrative topics:', error);
        toast.error('Error loading topics. Please refresh the page or try again.');
      }
    };

    void loadFilteredNarrativeTopicGroups();
  }, [isTopics, selectedTagsKey]);

  useEffect(() => {
    const previous = previousChatSessionIdFromUrlRef.current;
    previousChatSessionIdFromUrlRef.current = chatSessionIdFromUrl;

    if (chatSessionIdFromUrl) {
      if (previous != null && previous !== chatSessionIdFromUrl) {
        resetCanvasSelectionState({ shouldResetTopicFilters: true });
        setFlowId(null);
      }
      setSessionState((prev) => {
        if (prev.chatSessionId === chatSessionIdFromUrl) return prev;
        return { ...prev, chatSessionId: chatSessionIdFromUrl };
      });
      if (activePage === PAGES.LANDING) {
        setActivePage(PAGES.CHAT);
      }
      return;
    }

    if (!sessionState.chatSessionId) return;
    resetSessionState();
    setActivePage(defaultLandingPage);
  }, [
    activePage,
    chatSessionIdFromUrl,
    resetSessionState,
    resetCanvasSelectionState,
    sessionState.chatSessionId,
  ]);

  useEffect(() => {
    if (requestedInitialFlowId == null || !chatSessionIdFromUrl) return;

    const selectionKey = `${chatSessionIdFromUrl}:${requestedInitialFlowId}`;
    if (previousRequestedFlowSelectionRef.current === selectionKey) return;

    previousRequestedFlowSelectionRef.current = selectionKey;
    resetCanvasSelectionState({ shouldResetTopicFilters: false });
    setFlowId(requestedInitialFlowId);
  }, [chatSessionIdFromUrl, requestedInitialFlowId, resetCanvasSelectionState]);

  useEffect(() => {
    if (shouldShowWorkspace) return;
    setFlowId(null);
    setSynchronizedDraftFlowState(null);
  }, [shouldShowWorkspace]);

  useEffect(() => {
    if (!hasSelectedTopics || sessionState.chatSessionId) return;
    ensureChatSession();
  }, [ensureChatSession, hasSelectedTopics, sessionState.chatSessionId]);

  const loadNarrativeTopicTags = useCallback(() => {
    if (hasLoadedTopicTags || narrativeTopicTagsRequestRef.current) return () => {};

    const abortController = new AbortController();
    const request = (async () => {
      try {
        const tags = await readNarrativeTopicsTags({ signal: abortController.signal });
        if (abortController.signal.aborted) return;
        setHasTagLoadError(false);
        setAvailableTags(Array.isArray(tags) ? tags : []);
        setHasLoadedTopicTags(true);
      } catch (error) {
        if (abortController.signal.aborted) return;
        console.error('Failed to load narrative topic tags', error);
        setHasTagLoadError(true);
        setAvailableTags([]);
      } finally {
        narrativeTopicTagsRequestRef.current = null;
      }
    })();

    narrativeTopicTagsRequestRef.current = request;

    return () => {
      abortController.abort();
      narrativeTopicTagsRequestRef.current = null;
    };
  }, [hasLoadedTopicTags]);

  useEffect(() => {
    if (!isTopics) return undefined;

    return loadNarrativeTopicTags();
  }, [isTopics, loadNarrativeTopicTags]);

  const handleCloseToolWindow = useCallback(() => {
    setIsLeftPaneCollapsed(true);
  }, []);

  const handleTemplateSaved = useCallback(() => {
    setCustomTemplatesRefreshKey((previousKey) => previousKey + 1);
  }, []);

  const handleTemplateSelected = useCallback(
    async ({ activeTab = 'numerator', source = 'workspace', template }) => {
      const topicIds = getTemplateTopicIds(template);
      const optimisticFlowState = {
        editablePromptAnswers: {},
        narrativeName: template?.name || DEFAULT_DRAFT_NARRATIVE_NAME,
        topics: topicIds,
      };
      const rollbackState = {
        draftFlowState,
        flowId,
        isTemplateDirty,
        selectedTopicCards,
      };

      let chatSessionId = sessionState.chatSessionId;
      let requestId = null;

      try {
        const shouldCreateNewProject = source === 'full-page' && !shouldShowWorkspace;
        if (shouldCreateNewProject) {
          resetSessionState();
        }

        requestId = ++templateApplyRequestIdRef.current;

        if (!chatSessionId) {
          chatSessionId = await ensureChatSession();
        }

        if (requestId !== templateApplyRequestIdRef.current) return;
        if (!chatSessionId) return;

        setWorkspaceTemplateContext({
          activeTab: activeTab === 'custom' ? 'custom' : 'numerator',
          selectedTemplateId: template?.id ?? null,
        });
        handleChatSessionReady(chatSessionId);
      } catch (error) {
        if (requestId !== templateApplyRequestIdRef.current) return;
        console.error('Failed to create chat session from template selection', error);
        toast.error('We could not start a new project. Please try again.');
        return;
      }
      setSynchronizedDraftFlowState(optimisticFlowState);
      setFlowId(null);
      setSelectedTopicCards([]);
      setActivePage(PAGES.WORKSPACE_TEMPLATES);
      setIsLeftPaneCollapsed(false);
      setPendingTemplateSelection(null);

      try {
        await updateFlowState({
          chatSessionId,
          flowState: optimisticFlowState,
        });
        const updatedFlowState = await hydrateDynamicNarrativeFlowState({
          flowState: optimisticFlowState,
        });

        if (requestId !== templateApplyRequestIdRef.current) return;
        setSynchronizedDraftFlowState({
          ...(updatedFlowState || {}),
          narrativeName:
            updatedFlowState?.narrativeName ||
            optimisticFlowState?.narrativeName ||
            DEFAULT_DRAFT_NARRATIVE_NAME,
        });
        setIsTemplateDirty(false);
      } catch (error) {
        if (requestId !== templateApplyRequestIdRef.current) return;
        console.error('Failed to hydrate draft flow state from template selection', error);
        toast.error('We could not load that template. Please try again.');
        setFlowId(rollbackState.flowId);
        setSynchronizedDraftFlowState(rollbackState.draftFlowState);
        setIsTemplateDirty(rollbackState.isTemplateDirty);
        setSelectedTopicCards(rollbackState.selectedTopicCards);
      }
    },
    [
      draftFlowState,
      ensureChatSession,
      handleChatSessionReady,
      isTemplateDirty,
      resetSessionState,
      selectedTopicCards,
      sessionState.chatSessionId,
      shouldShowWorkspace,
      flowId,
    ]
  );

  const requestTemplateSelection = useCallback(
    async (selection) => {
      if (!selection?.template) return;
      if (flowId != null || (hasCanvasCards && isTemplateDirty)) {
        setPendingTemplateSelection(selection);
        return;
      }
      await handleTemplateSelected(selection);
    },
    [flowId, handleTemplateSelected, hasCanvasCards, isTemplateDirty]
  );

  return (
    <>
      <Flex className={styles['narratives-root']} gap="none">
        <NarrativesNavigation
          activePage={activePage}
          hasChatAccess={hasChatAccess}
          isCollapsed={isLeftPaneCollapsed}
          onClick={handlePageChange}
        />

        {isLanding && (
          <>
            <NarrativesChatLandingPage
              onNavigation={handlePageChange}
              onSubmit={handleLandingSubmit}
            />

            <IconButtonV2
              aria-label="Help"
              className={styles['help-button']}
              icon={<HelpIcon />}
              onClick={handleHelpClick}
              size="small"
              tooltip="Help"
              tooltipProps={{ placement: 'left' }}
              variant="subtle"
            />
          </>
        )}

        {isTemplates && (
          <Narratives
            hasTemplateAccess
            onBuildYourOwnClick={() => handlePageChange(PAGES.TOPICS)}
            onTemplateSelect={(selection) =>
              requestTemplateSelection({
                ...selection,
                source: 'full-page',
              })
            }
          />
        )}

        {shouldShowWorkspace && (
          <div className={styles['narratives-chat-shell']}>
            <div className={styles['narratives-chat-content']}>
              <div
                aria-hidden={isLeftPaneCollapsed}
                className={styles['narratives-chat-window']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-window"
                inert={isLeftPaneCollapsed ? '' : undefined}
              >
                {isChat && (
                  <NarrativesChatWindow
                    activeFlowId={flowId}
                    allowCanvasHydrationOnMount={shouldAllowCanvasHydrationOnMount}
                    chatSessionId={sessionState.chatSessionId}
                    initialErrorMessage={activeChatSnapshot?.errorMessage}
                    initialInput={initialInput}
                    initialInputValue={activeChatSnapshot?.inputValue}
                    initialMessages={activeChatSnapshot?.messages}
                    key={sessionState.chatSessionId || 'draft'}
                    liveFlows={websocketFlows}
                    narrativeNamesById={narrativeNamesById}
                    onChatSessionReady={handleChatSessionReady}
                    onCloseToolWindow={handleCloseToolWindow}
                    onFlowSelected={handleFlowSelected}
                    onFlowStateChange={handleFlowStateChange}
                    onMessageSent={handleChatMessageSent}
                    onNarrativeDeleted={handleProjectNarrativeDeleted}
                    onNarrativeNameChange={handleProjectNarrativeRenamed}
                    onStateSnapshot={handleChatStateSnapshot}
                    optimisticNarratives={
                      optimisticNarrativesBySession?.[sessionState.chatSessionId]
                    }
                    shouldBreakAwayFromRunOnMessage={Boolean(flowId)}
                    shouldHydrateOnMount={!activeChatSnapshot?.wasHydrated}
                    syncCanvasOnMount={!activeChatSnapshot?.wasHydrated}
                  />
                )}
                {isTopics && (
                  <NarrativesTopicsWindow
                    activeTopicIds={topicIdsForLibrary}
                    availableTags={availableTags}
                    hasTagLoadError={hasTagLoadError}
                    narrativeTopicGroups={visibleTopicGroups}
                    onCloseToolWindow={handleCloseToolWindow}
                    onInsertTopicCard={handleInsertTopicCard}
                    selectedTags={selectedTags}
                    setSelectedTags={setSelectedTags}
                  />
                )}
                {isWorkspaceTemplates && (
                  <NarrativesTemplatesWindow
                    customTemplatesRefreshKey={customTemplatesRefreshKey}
                    initialActiveTab={workspaceTemplateContext.activeTab}
                    initialActiveTemplateId={workspaceTemplateContext.selectedTemplateId}
                    onCloseToolWindow={handleCloseToolWindow}
                    onSelectTemplate={(selection) =>
                      requestTemplateSelection({
                        ...selection,
                        source: 'workspace',
                      })
                    }
                  />
                )}
                {isPastProjects && (
                  <NarrativesProjectHistory
                    activeDraftFlowState={draftFlowState}
                    activeFlowId={flowId}
                    activeSessionId={sessionState.chatSessionId}
                    deletedNarrativeIdsBySession={deletedNarrativeIdsBySession}
                    draftNarrativeNamesBySession={draftNarrativeNamesBySession}
                    favoriteStatusBySession={favoriteStatusBySession}
                    handleChatSessionReady={handleChatSessionReady}
                    handleFlowSelected={handleFlowSelected}
                    isWindowCollapsed={isLeftPaneCollapsed}
                    narrativeNamesById={narrativeNamesById}
                    onCloseToolWindow={handleCloseToolWindow}
                    onDraftDeleted={handleProjectDraftDeleted}
                    onDraftSelected={handleDraftSelected}
                    onFavoriteChange={handleProjectFavoriteSync}
                    onNarrativeDeleted={handleProjectNarrativeDeleted}
                    onNarrativeNameChange={handleProjectNarrativeRenamed}
                    onProjectNameChange={handleProjectNameSync}
                    projectNamesBySession={projectNamesBySession}
                    projectNarrativesBySession={projectNarrativesBySession}
                  />
                )}
              </div>

              <div
                className={styles['narratives-chat-canvas']}
                data-collapsed={isLeftPaneCollapsed}
                data-testid="narratives-chat-canvas"
              >
                <NarrativesCanvas
                  activeNarrativeName={flowId != null ? narrativeNamesById?.[flowId] || null : null}
                  activeTopicConfiguration={activeTopicConfiguration}
                  chatSessionId={sessionState.chatSessionId}
                  externalIsFavorite={favoriteStatusBySession?.[sessionState.chatSessionId] ?? null}
                  externalProjectName={projectNamesBySession?.[sessionState.chatSessionId]}
                  flowId={flowId}
                  flowState={draftFlowState}
                  isRunTopicMutationInProgress={isDeletingRunTopic}
                  onActiveTopicConfigurationChange={setActiveTopicConfiguration}
                  onEnsureChatSession={ensureChatSession}
                  onFavoriteChange={handleProjectFavoriteSync}
                  onFlowSelected={handleFlowSelected}
                  onFlowStateChange={handleFlowStateChange}
                  onInsertRunTopicConfiguration={requestInsertTopicCardBreakaway}
                  onNarrativeCreated={handleProjectNarrativeCreated}
                  onNarrativeDeleted={handleProjectNarrativeDeleted}
                  onNarrativeNameChange={handleProjectNarrativeRenamed}
                  onProjectNameChange={handleProjectNameSync}
                  onReplaceRunTopicConfiguration={breakAwayToDraftFlowStateWithReplacement}
                  onRequestTopicConfigurationLookup={loadNarrativeTopicGroups}
                  onRunFlowTopicIdsChange={handleRunFlowTopicIdsChange}
                  onSelectedTopicsChange={setSelectedTopicCards}
                  onTemplateSaved={handleTemplateSaved}
                  runFlowRefreshKey={runFlowRefreshKey}
                  selectedTopicCards={selectedTopicCards}
                  topicConfigurationLookup={topicConfigurationLookup}
                />
              </div>
            </div>
          </div>
        )}
      </Flex>
      <ConfirmActionModal
        confirmText="Remove"
        confirmVariant="error"
        isActive={Boolean(runTopicPendingDeletion)}
        isConfirmDisabled={isDeletingRunTopic}
        message="Are you sure you would like to remove this topic card? You'll lose its results."
        onCancel={() => setRunTopicPendingDeletion(null)}
        onConfirm={confirmRunTopicDeletion}
        title="Remove"
      />
      <ConfirmActionModal
        confirmText="Insert Topic Card"
        isActive={Boolean(pendingInsertTopicCard)}
        isCancelDisabled={isInsertingTopicCard}
        isConfirmDisabled={isInsertingTopicCard}
        message="This will add the topic card to the canvas and create a new draft narrative. If your project has an existing draft narrative, it will be deleted. Run narratives will not be affected."
        onCancel={() => {
          if (isInsertingTopicCard) return;
          closePendingInsertTopicCard(false);
        }}
        onConfirm={confirmPendingInsertTopicCard}
        title="Insert Topic Card?"
      />
      <ConfirmActionModal
        confirmText="Apply Template"
        isActive={Boolean(pendingTemplateSelection)}
        message={`This will replace the cards currently on your canvas with the cards from ${
          pendingTemplateSelection?.template?.name || 'this template'
        } and create a new draft narrative. If your project has an existing draft narrative, it will be deleted.`}
        onCancel={() => setPendingTemplateSelection(null)}
        onConfirm={() => handleTemplateSelected(pendingTemplateSelection)}
        title="Apply Template?"
      />
    </>
  );
};

NarrativesChat.propTypes = {
  hasChatAccess: PropTypes.bool,
};

export default NarrativesChat;
